import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Aviso } from '@/components/ui/aviso';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { adminService } from '../services/admin-service';
import type { OpcionesDeLaDemo } from '../types/admin.types';
import { TEXTOS_DE_LA_FICHA, TEXTOS_DEL_CAMBIO } from '../lib/demo-textos';
import { PastillasDeLaDemo } from './pastillas-de-la-demo';
import { CambiarRubroYTiendaDialog } from './cambiar-rubro-y-tienda-dialog';

/**
 * El rubro y la tienda de una empresa en prueba, en su ficha (343, tanda 4), con «Cambiar rubro y
 * tienda». Todo lo que dice sale del servidor (`GET /api/admin/tenants/:id/demo`).
 */
export function RubroYTiendaDeLaPrueba({
  tenantId,
  nombre,
  onCambio,
}: {
  tenantId: string;
  nombre: string;
  /** Se cambió el rubro o la tienda: la ficha puede releer lo que depende de eso. */
  onCambio?: () => void;
}) {
  const [opciones, setOpciones] = useState<OpcionesDeLaDemo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cambiando, setCambiando] = useState(false);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      setOpciones(await adminService.getOpcionesDeLaDemo(tenantId));
    } catch (err) {
      setError(mensajeDeError(err, TEXTOS_DEL_CAMBIO.noSeLeyo));
    }
  }, [tenantId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (error) return <Aviso tono="alerta">{error}</Aviso>;
  if (!opciones) return <Skeleton className="h-16" />;

  const { demo } = opciones;
  return (
    <div className="space-y-3">
      <dl className="grid gap-3 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">{TEXTOS_DE_LA_FICHA.rubro}</dt>
          <dd className="font-medium">{demo.eje?.nombre ?? TEXTOS_DE_LA_FICHA.sinRubro}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{TEXTOS_DE_LA_FICHA.tienda}</dt>
          <dd className="font-medium">
            {opciones.actual?.nombre ?? TEXTOS_DE_LA_FICHA.sinTienda}
            {demo.archivadas > 0 && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {TEXTOS_DE_LA_FICHA.guardadas(demo.archivadas)}
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{TEXTOS_DE_LA_FICHA.productosTitulo}</dt>
          <dd className="font-medium tabular-nums">
            {TEXTOS_DE_LA_FICHA.productos(demo.productos.usados, demo.productos.tope)}
          </dd>
        </div>
      </dl>
      <PastillasDeLaDemo demo={demo} misiones={opciones.misiones} />
      {demo.archivada ? (
        <p className="text-xs text-muted-foreground">{TEXTOS_DE_LA_FICHA.archivada}</p>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setCambiando(true)}>
          {TEXTOS_DEL_CAMBIO.boton}
        </Button>
      )}
      <CambiarRubroYTiendaDialog
        abierto={cambiando}
        empresaId={tenantId}
        nombre={nombre}
        opciones={opciones}
        onCerrar={() => setCambiando(false)}
        onCambiado={() => {
          void cargar();
          onCambio?.();
        }}
      />
    </div>
  );
}
